// src/components/SidebarGastos.jsx
import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebaseConfig";

/* ─── estilos para react-modal ─── */
const modalStyles = {
  content: {
    display: "flex",
    flexDirection: "column",
    top: "50%",
    left: "50%",
    transform: "translate(-50%,-50%)",
    width: "30%",
    minHeight: "200px",
    padding: 20,
    borderRadius: 10,
    border: "1px solid #ccc",
    boxShadow: "0 5px 15px rgba(0,0,0,.3)",
    fontFamily: "Inter, sans-serif",
  },
  overlay: {
    backgroundColor: "rgba(0,0,0,.4)",
    zIndex: 1000,
  },
  input: {
    width: "100%",
    padding: 8,
    borderRadius: 5,
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },
};

export default function SidebarGastos({
  ownerUid,
  idOrden,
  listas = [],
  setListas,
  onSelectLista = () => {},
  onListaEliminada = () => {},
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [listaName, setListaName] = useState("");
  const [filtro, setFiltro] = useState("");
  const [editId, setEditId] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const sidebarRef = useRef(null);

  // escucha Firestore
  useEffect(() => {
    if (!ownerUid || !idOrden) return;
    // definimos la ruta aquí para que no sea un "missing dependency"
    const listasPath = [
      "usuarios",
      ownerUid,
      "pedidos",
      idOrden,
      "listasGastos",
    ];
    const q = query(collection(db, ...listasPath), orderBy("createdAt", "asc"));
    const unsub = onSnapshot(q, (snap) =>
      setListas(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [ownerUid, idOrden, setListas]);

  // clic fuera para cerrar dropdown
  useEffect(() => {
    const handler = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setMenuOpen(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const openModal = () => setModalOpen(true);
  const closeModal = () => {
    setModalOpen(false);
    setListaName("");
    setEditId(null);
  };

  // crear o editar carpeta
  const guardarLista = async () => {
    if (!listaName.trim()) {
      return Swal.fire("Error", "Ingresa un nombre de lista", "error");
    }

    const listasPath = [
      "usuarios",
      ownerUid,
      "pedidos",
      idOrden,
      "listasGastos",
    ];

    if (editId) {
      await updateDoc(doc(db, ...listasPath, editId), { nombre: listaName });
      Swal.fire("Actualizado", "Lista editada correctamente", "success");
    } else {
      await addDoc(collection(db, ...listasPath), {
        nombre: listaName,
        createdAt: serverTimestamp(),
      });
      Swal.fire("Creada", "Lista creada exitosamente", "success");
    }

    closeModal();
  };

  const editarLista = (lista) => {
    setListaName(lista.nombre);
    setEditId(lista.id);
    setModalOpen(true);
  };

  // eliminar carpeta + sus gastos
  const eliminarLista = async (id) => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Eliminar carpeta?",
      text: "Esta acción eliminará la carpeta y sus productos.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!isConfirmed) return;

    const gastosPath = [
      "usuarios",
      ownerUid,
      "pedidos",
      idOrden,
      "listasGastos",
      id,
      "productosGastos",
    ];
    const snap = await getDocs(collection(db, ...gastosPath));
    if (!snap.empty) {
      const batch = writeBatch(db);
      snap.docs.forEach((d) => batch.delete(doc(db, ...gastosPath, d.id)));
      await batch.commit();
    }

    const listasPath = [
      "usuarios",
      ownerUid,
      "pedidos",
      idOrden,
      "listasGastos",
    ];
    await deleteDoc(doc(db, ...listasPath, id));
    Swal.fire("Eliminado", "Carpeta eliminada", "success");
    onListaEliminada(id);
  };

  const listasFiltradas = listas.filter((l) =>
    l.nombre.toLowerCase().includes(filtro.toLowerCase())
  );

  return (
    <div ref={sidebarRef} className="sidebar-gastos">
      {/* header */}
      <div className="header">
        <strong>Listas</strong>
        <button
          onClick={openModal}
          title="Agregar carpeta"
          className="add-lista-btn"
        >
          <i className="bi bi-plus" />
        </button>
      </div>

      {/* buscador */}
      <input
        className="input-lista"
        placeholder="Buscar carpeta…"
        value={filtro}
        onChange={(e) => setFiltro(e.target.value)}
      />

      {/* listado */}
      <ul className="lista-nombres">
        {listasFiltradas.map((lista) => (
          <li
            key={lista.id}
            onClick={() => onSelectLista(lista)}
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <span
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
              }}
            >
              <i className="bi bi-folder2-open" style={{ marginRight: 8 }} />
              {lista.nombre}
            </span>
            <div style={{ position: "relative" }}>
              <i
                className="bi bi-three-dots"
                style={{ cursor: "pointer", padding: 5 }}
                onClick={() =>
                  setMenuOpen(menuOpen === lista.id ? null : lista.id)
                }
              />
              <div
                className="custom-dropdown"
                style={{ display: menuOpen === lista.id ? "block" : "none" }}
              >
                <button
                  className="dd-item"
                  onClick={() => {
                    editarLista(lista);
                    setMenuOpen(null);
                  }}
                >
                  <i className="bi bi-pencil-fill" /> Editar
                </button>
                <button
                  className="dd-item dd-item--danger"
                  onClick={() => {
                    eliminarLista(lista.id);
                    setMenuOpen(null);
                  }}
                >
                  <i className="bi bi-trash-fill" /> Eliminar
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {/* modal */}
      <Modal isOpen={modalOpen} onRequestClose={closeModal} style={modalStyles}>
        <button className="modal-close" onClick={closeModal}>
          <i className="bi bi-x-lg" />
        </button>

        <h2 style={{ textAlign: "center", marginBottom: 20 }}>
          {editId ? "Editar Carpeta" : "Crear Nueva Carpeta"}
        </h2>

        <label style={{ display: "block", marginBottom: 8 }}>
          Nombre de la carpeta:
        </label>
        <input
          type="text"
          value={listaName}
          onChange={(e) => setListaName(e.target.value)}
          style={modalStyles.input}
        />

        <div style={{ flex: 1 }} />

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
          }}
        >
          <button
            onClick={closeModal}
            style={{
              padding: "8px 16px",
              background: "#ddd",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
            }}
          >
            Cancelar
          </button>
          <button
            onClick={guardarLista}
            style={{
              padding: "8px 16px",
              background: "#007bff",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
            }}
          >
            {editId ? "Guardar cambios" : "Crear carpeta"}
          </button>
        </div>
      </Modal>
    </div>
  );
}
